import React from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, ClickAwayListener } from '@material-ui/core';
import { Typography } from '@orijinworks/frontend-commons';
import MemberAutocomplete from '../../../../../Feeds/components/AdminLearnerModal/MemberAutocomplete';
import {
  formatResidentLabel,
  isPersonOptionEqual,
  passthroughFilterOptions,
} from '../../../../utils/case-plan-create.util';
import SectionTitle from '../SectionTitle';
import ExistingPlanChip from './ExistingPlanChip';
import SelectedResidentCard from './SelectedResidentCard';
import { useResidentSectionHook } from './useResidentSectionHook';
import { useResidentSectionStyles } from './ResidentSection.styles';

/**
 * Multi-select residents section: selected cards + search / Add resident control.
 * Presentational only — state and selection logic live in useResidentSectionHook.
 * Matches Figma Residents (967:21438 / 967:22953) and Selected Resident (967:21460).
 */
export const ResidentSection = ({ selectedResidents, onResidentsChange }) => {
  const classes = useResidentSectionStyles();
  const {
    isLoading,
    isSearchOpen,
    useGridLayout,
    memberOptions,
    selectedMembers,
    handleSearchChange,
    handleMembersChange,
    handleRemoveResident,
    handleAddResidentClick,
    handleSearchClickAway,
    getOptionDisabled,
  } = useResidentSectionHook(selectedResidents, onResidentsChange);

  return (
    <Box className={classes.root}>
      <SectionTitle title="Residents" disablePadding />
      <Box className={classes.body}>
        <Typography variant="body1" className={classes.helper}>
          When adding multiple residents, each resident gets their own plan,
          using the structure and editors below.
        </Typography>

        {selectedResidents.length > 0 ? (
          <Box
            className={
              useGridLayout ? classes.selectedListGrid : classes.selectedList
            }
          >
            {selectedResidents.map((resident) => (
              <SelectedResidentCard
                key={resident.personId || resident.username}
                resident={resident}
                onRemove={handleRemoveResident}
                compact={useGridLayout}
              />
            ))}
          </Box>
        ) : null}

        {isSearchOpen ? (
          <ClickAwayListener onClickAway={handleSearchClickAway}>
            <Box className={classes.search}>
              <MemberAutocomplete
                multiple
                freeSolo={false}
                label="Resident"
                placeholder="Search by name, username, or DOC ID"
                memberOptions={memberOptions}
                selectedMembers={selectedMembers}
                onMembersChange={handleMembersChange}
                onSearchChange={handleSearchChange}
                isMemberListLoading={isLoading}
                filterOptions={passthroughFilterOptions}
                getOptionLabel={formatResidentLabel}
                getOptionDisabled={getOptionDisabled}
                isOptionEqualToValue={isPersonOptionEqual}
                renderOptionEndAdornment={(option) =>
                  option.hasPlan ? <ExistingPlanChip /> : null
                }
                rootClassName="resident-autocomplete-root"
                dropdownFooterLeftText="Learners with existing plans cannot be selected."
              />
            </Box>
          </ClickAwayListener>
        ) : (
          <Button
            className={classes.addButton}
            variant="outlined"
            onClick={handleAddResidentClick}
            startIcon={<AddIcon className={classes.addButtonIcon} />}
          >
            Add resident
          </Button>
        )}
      </Box>
    </Box>
  );
};

ResidentSection.propTypes = {
  selectedResidents: PropTypes.arrayOf(PropTypes.object),
  onResidentsChange: PropTypes.func.isRequired,
};

ResidentSection.defaultProps = {
  selectedResidents: [],
};

export default ResidentSection;
